<template>
    <div class="fm-main">
        <div :class="['fm-toolbar', {'fm-toolbar--has-selection': showSelectionBar}]">
            <button class="fm-nav-btn" :disabled="!fm.canGoBack.value" @click="fm.goBack()" v-tooltip="$t('common.fileManager.goBack')">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
            </button>
            <button class="fm-nav-btn" :disabled="!fm.canGoForward.value" @click="fm.goForward()" v-tooltip="$t('common.fileManager.goForward')">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
            </button>
            <div v-if="!(isCompact && compactSearchOpen)" class="fm-breadcrumb">
                <template v-for="(crumb, i) in fm.breadcrumbs.value" :key="crumb.id">
                    <span v-if="i > 0" class="fm-breadcrumb-sep">/</span>
                    <span :class="['fm-breadcrumb-item', {'fm-breadcrumb-item--current': i === fm.breadcrumbs.value.length - 1}]"
                          @click="i < fm.breadcrumbs.value.length - 1 && fm.navigateToFolder(crumb.id)">
                        {{ crumb.nameKey ? $t(crumb.nameKey) : crumb.name }}
                    </span>
                </template>
            </div>

            <template v-if="isCompact">
                <div v-if="compactSearchOpen" class="fm-toolbar-search fm-toolbar-search--expanded">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                    <input ref="compactSearchInput" type="text" :placeholder="$t('common.search') || 'Ara...'" :value="fm.state.searchQuery" @input="fm.search($event.target.value)"/>
                    <button class="fm-toolbar-search-close" @click="closeCompactSearch">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                </div>
                <button v-else class="fm-toolbar-btn" @click="openCompactSearch">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                </button>
            </template>
            <div v-else class="fm-toolbar-search">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                <input type="text" :placeholder="$t('common.search') || 'Ara...'" :value="fm.state.searchQuery" @input="fm.search($event.target.value)"/>
            </div>

            <div class="fm-toolbar-divider"></div>

            <div v-if="fm.state.viewMode === 'grid'" class="fm-sort-dropdown" @click.stop>
                <button class="fm-toolbar-btn" @click="showSortMenu = !showSortMenu" v-tooltip="$t('common.fileManager.sort')">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 5h10"/><path d="M11 9h7"/><path d="M11 13h4"/><path d="M3 17l3 3 3-3"/><path d="M6 18V4"/></svg>
                </button>
                <div v-if="showSortMenu" class="fm-sort-menu">
                    <button v-for="s in sortOptions" :key="s.key" :class="['fm-sort-menu-item', {'fm-sort-menu-item--active': fm.state.sortBy === s.key}]" @click="fm.setSort(s.key); showSortMenu = false">
                        {{ s.label }}
                        <svg v-if="fm.state.sortBy === s.key" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                            <path v-if="fm.state.sortDir === 'asc'" d="M12 5v14M5 12l7-7 7 7"/>
                            <path v-else d="M12 19V5M5 12l7 7 7-7"/>
                        </svg>
                    </button>
                </div>
            </div>

            <template v-if="!isCompact">
                <button :class="['fm-toolbar-btn', {'fm-toolbar-btn--active': fm.state.viewMode === 'grid'}]"
                        @click="fm.setViewMode('grid')" v-tooltip="$t('common.fileManager.gridView')">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>
                </button>
                <button :class="['fm-toolbar-btn', {'fm-toolbar-btn--active': fm.state.viewMode === 'list'}]"
                        @click="fm.setViewMode('list')" v-tooltip="$t('common.fileManager.listView')">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="4" cy="7" r="1" fill="currentColor" stroke="none"/><circle cx="4" cy="12" r="1" fill="currentColor" stroke="none"/><circle cx="4" cy="17" r="1" fill="currentColor" stroke="none"/><line x1="9" y1="7" x2="20" y2="7"/><line x1="9" y1="12" x2="20" y2="12"/><line x1="9" y1="17" x2="20" y2="17"/></svg>
                </button>

                <button v-if="!pickerMode" :class="['fm-toolbar-btn', {'fm-toolbar-btn--active': fm.state.autoPreview}]"
                        @click="fm.toggleAutoPreview()" v-tooltip="$t('common.fileManager.autoPreview')">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>
                </button>

                <template v-if="showUploadBtn">
                    <div class="fm-toolbar-divider"></div>
                    <button class="fm-toolbar-btn" @click="$emit('upload')" v-tooltip="$t('common.fileManager.upload')">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                    </button>
                </template>

                <button v-if="!pickerMode && showNewFolderBtn" class="fm-toolbar-btn" @click="onNewFolder" v-tooltip="$t('common.fileManager.newFolder')">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/><line x1="12" y1="11" x2="12" y2="17"/><line x1="9" y1="14" x2="15" y2="14"/></svg>
                </button>

                <template v-if="fm.isTrashMode.value && hasTrashItems">
                    <div class="fm-toolbar-divider"></div>
                    <button class="fm-toolbar-btn" @click="$emit('restoreAll')" v-tooltip="$t('common.fileManager.restoreAll')">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg>
                    </button>
                    <button class="fm-toolbar-btn fm-toolbar-btn--danger" @click="$emit('emptyTrash')" v-tooltip="$t('common.fileManager.emptyTrash')">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
                    </button>
                </template>
            </template>

            <div v-else class="fm-sort-dropdown" @click.stop>
                <button class="fm-toolbar-btn" @click="showMoreMenu = !showMoreMenu; showSortMenu = false">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/><line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/><line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/><line x1="1" y1="14" x2="7" y2="14"/><line x1="9" y1="8" x2="15" y2="8"/><line x1="17" y1="16" x2="23" y2="16"/></svg>
                </button>
                <div v-if="showMoreMenu" class="fm-sort-menu fm-compact-menu">
                    <div class="fm-compact-menu-section">{{ $t('common.fileManager.viewMode') }}</div>
                    <div class="fm-compact-menu-row">
                        <button :class="['fm-compact-menu-toggle', {'fm-compact-menu-toggle--active': fm.state.viewMode === 'grid'}]"
                                @click="fm.setViewMode('grid'); showMoreMenu = false">
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>
                        </button>
                        <button :class="['fm-compact-menu-toggle', {'fm-compact-menu-toggle--active': fm.state.viewMode === 'list'}]"
                                @click="fm.setViewMode('list'); showMoreMenu = false">
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="4" cy="7" r="1" fill="currentColor" stroke="none"/><circle cx="4" cy="12" r="1" fill="currentColor" stroke="none"/><circle cx="4" cy="17" r="1" fill="currentColor" stroke="none"/><line x1="9" y1="7" x2="20" y2="7"/><line x1="9" y1="12" x2="20" y2="12"/><line x1="9" y1="17" x2="20" y2="17"/></svg>
                        </button>
                    </div>
                    <button v-if="!pickerMode" class="fm-compact-menu-item" @click="fm.toggleAutoPreview(); showMoreMenu = false">
                        <span class="fm-compact-menu-icon">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>
                        </span>
                        {{ $t('common.fileManager.autoPreview') }}
                        <span :class="['fm-compact-menu-check', {'fm-compact-menu-check--on': fm.state.autoPreview}]">
                            <svg v-if="fm.state.autoPreview" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><polyline points="20 6 9 17 4 12"/></svg>
                        </span>
                    </button>
                    <template v-if="showUploadBtn || (!pickerMode && showNewFolderBtn)">
                        <div class="fm-context-divider"></div>
                        <button v-if="showUploadBtn" class="fm-compact-menu-item" @click="$emit('upload'); showMoreMenu = false">
                            <span class="fm-compact-menu-icon">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                            </span>
                            {{ $t('common.fileManager.upload') }}
                        </button>
                        <button v-if="!pickerMode && showNewFolderBtn" class="fm-compact-menu-item" @click="onNewFolder(); showMoreMenu = false">
                            <span class="fm-compact-menu-icon fm-icon-color--folder">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/><line x1="12" y1="11" x2="12" y2="17"/><line x1="9" y1="14" x2="15" y2="14"/></svg>
                            </span>
                            {{ $t('common.fileManager.newFolder') }}
                        </button>
                    </template>
                    <template v-if="fm.isTrashMode.value && hasTrashItems">
                        <div class="fm-context-divider"></div>
                        <button class="fm-compact-menu-item" @click="$emit('restoreAll'); showMoreMenu = false">
                            <span class="fm-compact-menu-icon">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg>
                            </span>
                            {{ $t('common.fileManager.restoreAll') }}
                        </button>
                        <button class="fm-compact-menu-item fm-compact-menu-item--danger" @click="$emit('emptyTrash'); showMoreMenu = false">
                            <span class="fm-compact-menu-icon">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
                            </span>
                            {{ $t('common.fileManager.emptyTrash') }}
                        </button>
                    </template>
                </div>
            </div>
        </div>

        <transition name="fm-selection-slide">
            <div v-if="showSelectionBar" class="fm-selection-bar" @click="showSelectionMoveMenu = false">
                <button class="fm-selection-chip" @click="fm.clearSelection()" v-tooltip="$t('common.fileManager.deselect')">
                    <strong>{{ fm.state.selectedIds.length }}</strong> {{ $t('common.fileManager.itemsSelected') }}
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
                <span class="fm-selection-spacer"></span>
                <button v-if="selectionHasDownloadable" class="fm-selection-btn fm-selection-btn--action" @click="$emit('downloadSelected')" v-tooltip="$t('common.fileManager.download')">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                    <span class="fm-selection-btn-label">{{ $t('common.fileManager.download') }}</span>
                </button>
                <div v-if="selectionCanMove" class="fm-selection-move-wrap" @click.stop>
                    <button class="fm-selection-btn fm-selection-btn--action" @click="showSelectionMoveMenu = !showSelectionMoveMenu" v-tooltip="$t('common.fileManager.move')">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                        <span class="fm-selection-btn-label">{{ $t('common.fileManager.move') }}</span>
                    </button>
                    <div v-if="showSelectionMoveMenu" class="fm-selection-move-menu">
                        <button v-if="fm.state.currentFolderId" class="fm-selection-move-item" @click="onSelectionMove(null)">
                            <svg class="fm-move-folder-icon" viewBox="0 0 24 24" fill="none" stroke-width="1.5"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>
                            {{ $t('common.fileManager.rootDirectory') }}
                        </button>
                        <div v-if="fm.state.currentFolderId && selectionMoveFolders.length" class="fm-context-divider"></div>
                        <button v-for="folder in selectionMoveFolders" :key="folder.id" class="fm-selection-move-item" @click="onSelectionMove(folder.id)">
                            <svg class="fm-move-folder-icon" viewBox="0 0 24 24" fill="none" stroke-width="1.5"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>
                            {{ folder.name }}
                        </button>
                    </div>
                </div>
                <button v-if="selectionCanDelete" class="fm-selection-btn fm-selection-btn--danger" @click="$emit('deleteItems', [...fm.state.selectedIds])" v-tooltip="$t('common.fileManager.delete')">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                    <span class="fm-selection-btn-label">{{ $t('common.fileManager.delete') }}</span>
                </button>
            </div>
        </transition>

        <div :class="['fm-content-scroll', {'fm-content-scroll--drop-target': contentDropHighlight, 'fm-content-scroll--has-picker': showPickerBar}]"
             ref="contentScroll" tabindex="0"
             @contextmenu.prevent="onContentContextMenu"
             @click="onContentClick"
             @keydown="onKeyDown"
             @mousedown="onContentMouseDown"
             @dragover.prevent="onContentDragOver"
             @dragleave="onContentDragLeave"
             @drop.prevent="onContentDrop">

            <template v-if="fm.state.loading || fm.state.searchLoading">
                <div v-if="fm.state.viewMode === 'grid'" class="fm-grid">
                    <div v-for="i in 8" :key="'skel-g-'+i" class="fm-grid-item fm-skeleton-item">
                        <div class="fm-grid-thumb fm-skeleton-box"></div>
                        <div class="fm-skeleton-text"></div>
                    </div>
                </div>
                <div v-else class="fm-list">
                    <div class="fm-list-header">
                        <span>{{ $t('common.fileManager.name') }}</span><span>{{ $t('common.fileManager.size') }}</span><span>{{ $t('common.fileManager.type') }}</span><span>{{ $t('common.fileManager.date') }}</span>
                    </div>
                    <div v-for="i in 6" :key="'skel-l-'+i" class="fm-list-row fm-skeleton-row">
                        <div class="fm-list-name">
                            <div class="fm-skeleton-circle"></div>
                            <div class="fm-skeleton-text fm-skeleton-text--wide"></div>
                        </div>
                        <div class="fm-skeleton-text fm-skeleton-text--sm"></div>
                        <div class="fm-skeleton-text fm-skeleton-text--sm"></div>
                        <div class="fm-skeleton-text fm-skeleton-text--sm"></div>
                    </div>
                </div>
            </template>

            <template v-else-if="allItems.length === 0">
                <div class="fm-empty">
                    <div class="fm-empty-icon">
                        <svg v-if="fm.state.searchQuery" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                        <svg v-else-if="fm.isTrashMode.value" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                        <svg v-else-if="isModuleFolder" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                        <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>
                    </div>
                    <div class="fm-empty-text">{{ fm.state.searchQuery ? $t('common.fileManager.noResults') : fm.isTrashMode.value ? $t('common.fileManager.trashEmpty') : isModuleFolder ? $t('common.fileManager.moduleEmpty') : $t('common.fileManager.folderEmpty') }}</div>
                    <div v-if="!fm.state.searchQuery && fm.isTrashMode.value" class="fm-empty-hint">{{ $t('common.fileManager.trashHint') }}</div>
                    <div v-else-if="!fm.state.searchQuery && !isModuleFolder" class="fm-empty-hint">{{ $t('common.fileManager.uploadHint') }}</div>
                    <button v-if="!fm.state.searchQuery && showUploadBtn && !fm.isTrashMode.value" class="fm-empty-upload" @click="$emit('upload')">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                        {{ $t('common.fileManager.uploadFile') }}
                    </button>
                </div>
            </template>

            <template v-else-if="fm.state.viewMode === 'grid'">
                <div class="fm-grid">
                    <div v-for="(item, idx) in allItems" :key="item.uid"
                         :class="gridItemClass(item, idx)"
                         :data-uid="item.uid"
                         :data-folder-id="item.type === 'folder' ? item.id : undefined"
                         :draggable="isDragEnabled"
                         @click="onItemClick($event, item, idx)"
                         @dblclick="onItemDblClick(item)"
                         @contextmenu.prevent.stop="onItemContextMenu($event, item, idx)"
                         @dragstart="onDragStart($event, item)"
                         @dragover.prevent="onItemDragOver($event, item)"
                         @dragleave="onItemDragLeave(item)"
                         @drop.prevent="onItemDrop($event, item)">
                        <div :class="['fm-grid-thumb', {
                            'fm-grid-thumb--folder': item.type === 'folder',
                            'fm-grid-thumb--file': item.type !== 'folder' && !hasThumb(item)
                        }]" :style="item.type !== 'folder' && !hasThumb(item) ? {'--file-type-color': getFileTypeColor(item)} : null">
                            <template v-if="hasThumb(item)">
                                <img :src="item.thumbnailUrl || item.publicUrl" :alt="item.name" draggable="false"/>
                                <div v-if="getIconType(item) === 'audio'" class="fm-grid-audio-overlay">
                                    <span class="fm-icon-color--audio" v-html="getItemIcon(item)"></span>
                                    <span class="fm-grid-file-ext">{{ (item.extension || '').toUpperCase() }}</span>
                                </div>
                            </template>
                            <template v-else-if="item.type === 'folder'">
                                <div class="fm-folder-macos" :style="{'--folder-color': item.color || '#3B82F6'}">
                                    <div class="fm-folder-tab"></div>
                                    <div :class="['fm-folder-body', {'fm-folder-body--open': !item.moduleIcon && isFolderNonEmpty(item)}]">
                                        <span v-if="item.moduleIcon" class="fm-folder-emblem" v-html="item.moduleIcon"></span>
                                    </div>
                                </div>
                            </template>
                            <template v-else>
                                <span :class="'fm-icon-color--' + getIconType(item)" v-html="getItemIcon(item)"></span>
                                <span v-if="item.extension" class="fm-grid-file-ext">{{ item.extension.toUpperCase() }}</span>
                            </template>
                        </div>
                        <input v-if="renamingId === item.uid" class="fm-grid-name-input"
                               ref="renameInput"
                               :value="renameValue"
                               @input="renameValue = $event.target.value"
                               @keydown.enter.stop="finishRename"
                               @keydown.escape="cancelRename"
                               @blur="finishRename"
                               @click.stop/>
                        <div v-else class="fm-grid-name">{{ item.nameKey ? $t(item.nameKey) : item.name }}</div>
                        <span v-if="isPickerTarget(item)" class="fm-picker-badge"></span>
                    </div>
                </div>
            </template>

            <template v-else>
                <div class="fm-list">
                    <div class="fm-list-header">
                        <span @click="fm.setSort('name')">
                            <span class="fm-list-header-sort">{{ $t('common.fileManager.name') }}
                                <svg v-if="fm.state.sortBy === 'name'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                                    <path v-if="fm.state.sortDir === 'asc'" d="M12 5v14M5 12l7-7 7 7"/>
                                    <path v-else d="M12 19V5M5 12l7 7 7-7"/>
                                </svg>
                            </span>
                        </span>
                        <span @click="fm.setSort('size')">
                            <span class="fm-list-header-sort">{{ $t('common.fileManager.size') }}
                                <svg v-if="fm.state.sortBy === 'size'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                                    <path v-if="fm.state.sortDir === 'asc'" d="M12 5v14M5 12l7-7 7 7"/>
                                    <path v-else d="M12 19V5M5 12l7 7 7-7"/>
                                </svg>
                            </span>
                        </span>
                        <span @click="fm.setSort('type')">
                            <span class="fm-list-header-sort">{{ $t('common.fileManager.type') }}
                                <svg v-if="fm.state.sortBy === 'type'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                                    <path v-if="fm.state.sortDir === 'asc'" d="M12 5v14M5 12l7-7 7 7"/>
                                    <path v-else d="M12 19V5M5 12l7 7 7-7"/>
                                </svg>
                            </span>
                        </span>
                        <span @click="fm.setSort('date')">
                            <span class="fm-list-header-sort">{{ $t('common.fileManager.date') }}
                                <svg v-if="fm.state.sortBy === 'date'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                                    <path v-if="fm.state.sortDir === 'asc'" d="M12 5v14M5 12l7-7 7 7"/>
                                    <path v-else d="M12 19V5M5 12l7 7 7-7"/>
                                </svg>
                            </span>
                        </span>
                    </div>
                    <div v-for="(item, idx) in allItems" :key="item.uid"
                         :class="listRowClass(item, idx)"
                         :data-uid="item.uid"
                         :data-folder-id="item.type === 'folder' ? item.id : undefined"
                         :draggable="isDragEnabled"
                         @click="onItemClick($event, item, idx)"
                         @dblclick="onItemDblClick(item)"
                         @contextmenu.prevent.stop="onItemContextMenu($event, item, idx)"
                         @dragstart="onDragStart($event, item)"
                         @dragover.prevent="onItemDragOver($event, item)"
                         @dragleave="onItemDragLeave(item)"
                         @drop.prevent="onItemDrop($event, item)">
                        <div class="fm-list-name">
                            <span v-if="item.type === 'folder'" class="fm-list-icon" :style="{color: item.color || '#3B82F6'}" v-html="getListFolderIcon(item)"></span>
                            <img v-else-if="hasThumb(item)" class="fm-list-thumb" :src="item.thumbnailUrl || item.publicUrl" :alt="item.name" draggable="false"/>
                            <span v-else :class="['fm-list-icon', 'fm-icon-color--' + getIconType(item)]" v-html="getItemIcon(item)"></span>
                            <input v-if="renamingId === item.uid" class="fm-list-name-input"
                                   ref="renameInput"
                                   :value="renameValue"
                                   @input="renameValue = $event.target.value"
                                   @keydown.enter.stop="finishRename"
                                   @keydown.escape="cancelRename"
                                   @blur="finishRename"
                                   @click.stop/>
                            <span v-else class="fm-list-filename">{{ item.nameKey ? $t(item.nameKey) : item.name }}</span>
                            <span v-if="isPickerTarget(item)" class="fm-picker-badge fm-picker-badge--list"></span>
                            <span v-if="item.module" :class="['fm-list-tag', {'fm-list-tag--clickable': !!item.moduleEntityId}]"
                                  @click.stop="navigateToEntity(item)">{{ getModuleLabel(item) }}</span>
                        </div>
                        <span class="fm-list-size">{{ item.type === 'folder' ? folderSizeLabel(item) : fm.formatFileSize(item.size) }}</span>
                        <span class="fm-list-type">{{ item.type === 'folder' ? $t('common.fileManager.folder') : (item.extension || '').toUpperCase() }}</span>
                        <span class="fm-list-date">{{ fm.formatDate(item.updatedAt || item.createdAt) || '–' }}</span>
                    </div>
                </div>
            </template>

            <div v-if="lassoActive" class="fm-lasso-box" :style="lassoStyle"></div>

            <div v-if="(fm.state.fileLoading || fm.state.entityLoading) && !fm.state.loading" class="fm-infinite-loader">
                <span class="fm-infinite-spinner"></span>
            </div>
            <div ref="infiniteSentinel" class="fm-infinite-sentinel"></div>
        </div>

        <transition name="fm-picker-slide">
            <div v-if="showPickerBar" class="fm-picker-bar" :class="{'fm-picker-bar--manage': pickerEntityFolder}">
                <template v-if="pickerEntityFolder">
                    <span class="fm-picker-count">{{ $t('common.fileManager.filesSelected', {count: fm.state.selectedIds.length}) }}</span>
                    <span v-if="!isCompact" class="fm-picker-dot">·</span>
                    <button class="fm-picker-btn fm-picker-btn--deselect" @click="fm.clearSelection()">
                        <svg v-if="isCompact" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                        <template v-else>{{ $t('common.fileManager.deselect') }}</template>
                    </button>
                    <span class="fm-picker-spacer"></span>
                    <button v-if="pickerCanDetach" class="fm-picker-btn fm-picker-btn--detach" @click="$emit('pickerDetach')">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17 7l-10 10"/><path d="M8 2v4"/><path d="M2 8h4"/><path d="M22 16h-4"/><path d="M16 22v-4"/></svg>
                        {{ $t('common.fileManager.detach') }}
                    </button>
                    <button v-if="pickerCanDelete" class="fm-picker-btn fm-picker-btn--delete" @click="$emit('pickerDelete')">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                        {{ $t('common.fileManager.delete') }}
                    </button>
                </template>
                <template v-else>
                    <span class="fm-picker-count">{{ $t('common.fileManager.filesSelected', {count: fm.state.selectedIds.length}) }}</span>
                    <button class="fm-picker-btn fm-picker-btn--cancel" @click="$emit('pickerCancel')">{{ $t('common.fileManager.cancel') }}</button>
                    <button class="fm-picker-btn fm-picker-btn--select" :disabled="fm.state.selectedIds.length === 0" @click="$emit('pickerSelect')">{{ $t('common.fileManager.select') }}</button>
                </template>
            </div>
        </transition>
    </div>
</template>

<script>
import fileManagerService from './fileManagerService'

export default {
    name: 'FileManagerContent',
    inject: { panelVm: { default: null } },
    props: {
        pickerMode: { type: Boolean, default: false },
        pickerAccept: { type: String, default: null },
        pickerMultiple: { type: Boolean, default: true },
        pickerEntityFolder: { type: Boolean, default: false },
        pickerCanDetach: { type: Boolean, default: false },
        pickerCanDelete: { type: Boolean, default: false },
    },
    emits: ['contextMenu', 'closeContextMenu', 'upload', 'pickerCancel', 'pickerSelect', 'pickerDetach', 'pickerDelete', 'preview', 'dragUpload', 'deleteItems', 'permanentDelete', 'lightbox', 'depthLimit', 'restoreAll', 'emptyTrash', 'downloadSelected', 'moveSelected'],
    data() {
        return {
            renamingId: null,
            renameValue: '',
            focusedIndex: -1,
            dropTargetId: null,
            showSortMenu: false,
            showMoreMenu: false,
            compactSearchOpen: false,
            touchDragReadyId: null,
            lassoActive: false,
            lassoRect: null,
            contentDropHighlight: false,
            showSelectionMoveMenu: false,
        }
    },
    mounted() {
        this._calcPageLimit()

        this.$options._resizeObs = new ResizeObserver(() => this._calcPageLimit())
        if (this.$refs.contentScroll) {
            this.$options._resizeObs.observe(this.$refs.contentScroll)
        }

        this.$options._infiniteObs = new IntersectionObserver((entries) => {
            if (entries[0]?.isIntersecting) {
                fileManagerService.loadMoreFiles()
                fileManagerService.loadMoreEntities()
            }
        }, { root: this.$refs.contentScroll, threshold: 0 })

        if (this.$refs.infiniteSentinel) {
            this.$options._infiniteObs.observe(this.$refs.infiniteSentinel)
        }

        this._unwatchReobserve = this.$watch(
            () => fileManagerService.state.files.length + fileManagerService.state.folders.length,
            () => this.$nextTick(() => this._reobserveSentinel())
        )

        this._initTouchLongPress()
    },
    beforeUnmount() {
        if (this.$options._resizeObs) {
            this.$options._resizeObs.disconnect()
            this.$options._resizeObs = null
        }
        if (this.$options._infiniteObs) {
            this.$options._infiniteObs.disconnect()
            this.$options._infiniteObs = null
        }
        if (this._unwatchReobserve) {
            this._unwatchReobserve()
        }
        this._stopDragAutoScroll()
        this._destroyTouchLongPress()
    },
    computed: {
        fm() { return fileManagerService },
        folderOpenSvg() {
            return '<svg viewBox="0 0 24 24" fill="currentColor" stroke="none"><path d="M2 6a2 2 0 0 1 2-2h4.59a1 1 0 0 1 .7.29L11 6H20a2 2 0 0 1 2 2v1H6.5a2 2 0 0 0-1.94 1.52L2 19V6z" opacity="0.5"/><path d="M4.5 9h17a1 1 0 0 1 .97 1.24l-2.1 8.4A2 2 0 0 1 18.44 20H4a2 2 0 0 1-1.94-2.48L4.06 10A1 1 0 0 1 4.5 9z"/></svg>'
        },
        folderClosedSvg() {
            return '<svg viewBox="0 0 24 24" fill="currentColor" stroke="none"><path d="M2 6a2 2 0 0 1 2-2h4.59a1 1 0 0 1 .7.29L11 6H20a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6z"/></svg>'
        },
        isCompact() {
            return this.panelVm?.isCompact || false
        },
        isModuleFolder() {
            const f = fileManagerService.state.currentFolderId
                ? fileManagerService.state.folders.find(fl => fl.id === fileManagerService.state.currentFolderId)
                : null
            return !!(f?.moduleType)
        },
        sortOptions() {
            return [
                { key: 'name', label: this.$t('common.fileManager.name') },
                { key: 'size', label: this.$t('common.fileManager.size') },
                { key: 'type', label: this.$t('common.fileManager.type') },
                { key: 'date', label: this.$t('common.fileManager.date') },
            ]
        },
        allItems() {
            return [...this.fm.currentFolders.value, ...this.fm.currentFiles.value]
        },
        isDragEnabled() {
            if (this.pickerMode) return false
            if (fileManagerService.isTrashMode.value) return false
            const f = this.currentFolderObj
            if (f && (f.moduleType || f.entityId)) return false
            const cat = fileManagerService.state.activeCategory
            return cat === 'ALL' || cat === 'UNATTACHED'
        },
        lassoStyle() {
            if (!this.lassoRect) return {}
            const r = this.lassoRect
            return {
                left: r.left + 'px',
                top: r.top + 'px',
                width: r.width + 'px',
                height: r.height + 'px',
            }
        },
        currentFolderObj() {
            const id = fileManagerService.state.currentFolderId
            return id ? fileManagerService.state.folders.find(f => f.id === id) : null
        },
        showUploadBtn() {
            if (fileManagerService.isTrashMode.value) return false
            if (this.pickerMode) return this.pickerEntityFolder
            const f = this.currentFolderObj
            if (!f) return true
            if (f.moduleType) return false
            if (f.entityId) return f.directUpload !== false
            return true
        },
        showNewFolderBtn() {
            if (fileManagerService.isTrashMode.value) return false
            const f = this.currentFolderObj
            if (!f) return fileManagerService.state.activeCategory === 'ALL'
            return !f.moduleType && !f.entityId
        },
        hasTrashItems() {
            return fileManagerService.state.files.length > 0 || fileManagerService.state.trashFolders.length > 0
        },
        showPickerBar() {
            return this.pickerMode && !fileManagerService.state.loading && !this.isModuleFolder && (!this.pickerEntityFolder || fileManagerService.state.selectedIds.length > 0)
        },
        showSelectionBar() {
            if (this.pickerMode) return false
            if (fileManagerService.isTrashMode.value) return false
            const sel = fileManagerService.state.selectedIds
            if (sel.length < 2) return false
            const uids = new Set(sel)
            const realCount = this.allItems.filter(i => uids.has(i.uid) && !i.moduleType && !i.entityId).length
            return realCount > 1
        },
        selectionHasDownloadable() {
            const uids = new Set(fileManagerService.state.selectedIds)
            return this.allItems.some(i => uids.has(i.uid) && i.type === 'file' && i.publicUrl && i.publicUrl !== '#')
        },
        selectionCanDelete() {
            const uids = new Set(fileManagerService.state.selectedIds)
            return this.allItems.some(i => uids.has(i.uid) && !i.moduleType && !i.entityId)
        },
        selectionCanMove() {
            const f = this.currentFolderObj
            if (f && (f.moduleType || f.entityId)) return false
            if (!f && fileManagerService.state.activeCategory !== 'ALL') return false
            return this.selectionMoveFolders.length > 0 || !!fileManagerService.state.currentFolderId
        },
        selectionMoveFolders() {
            const excluded = this._selectionExcludedFolderIds
            return fileManagerService.state.folders.filter(f => !f.moduleType && !f.entityId && !excluded.has(f.id))
        },
        _selectionExcludedFolderIds() {
            const set = new Set()
            const uids = new Set(fileManagerService.state.selectedIds)
            const folders = fileManagerService.state.folders
            const currentId = fileManagerService.state.currentFolderId
            if (currentId) set.add(currentId)
            for (const item of this.allItems) {
                if (!uids.has(item.uid) || item.type !== 'folder') continue
                set.add(item.id)
                const addDesc = (pid) => {
                    for (const f of folders) {
                        if (f.parentId === pid && !f.moduleType && !f.entityId) {
                            set.add(f.id)
                            addDesc(f.id)
                        }
                    }
                }
                addDesc(item.id)
            }
            return set
        },
    },
    methods: {
        isAccepted(item) {
            if (!this.pickerMode || !this.pickerAccept) return true
            return fileManagerService.matchesAccept(item, this.pickerAccept)
        },
        isPickerTarget(item) {
            if (!this.pickerMode || !fileManagerService.state._pickerEntity) return false
            return item.type === 'folder' && item.id === fileManagerService.state._pickerEntity.id
        },
        gridItemClass(item, idx) {
            return ['fm-grid-item', {
                'fm-grid-item--selected': this.isSelected(item.uid),
                'fm-grid-item--focused': this.focusedIndex === idx,
                'fm-grid-item--drop-target': this.dropTargetId === item.id && item.type === 'folder',
                'fm-grid-item--touch-drag-ready': this.touchDragReadyId === item.uid,
                'fm-grid-item--disabled': !this.isAccepted(item),
            }]
        },
        listRowClass(item, idx) {
            return ['fm-list-row', {
                'fm-list-row--selected': this.isSelected(item.uid),
                'fm-list-row--focused': this.focusedIndex === idx,
                'fm-list-row--drop-target': this.dropTargetId === item.id && item.type === 'folder',
                'fm-list-row--touch-drag-ready': this.touchDragReadyId === item.uid,
                'fm-list-row--disabled': !this.isAccepted(item),
            }]
        },
        isSelected(uid) {
            return this.fm.state.selectedIds.includes(uid)
        },
        isFolderNonEmpty(item) {
            if (item.fileCount > 0 || item.childrenCount > 0) return true
            if (item.children && item.children.length > 0) return true
            return fileManagerService.state.folders.some(f => f.parentId === item.id)
        },
        getListFolderIcon(item) {
            if (item.moduleIcon) return item.moduleIcon
            if (item.moduleType || item.entityId || this.isFolderNonEmpty(item)) return this.folderOpenSvg
            return this.folderClosedSvg
        },
        getIconType(item) {
            if (item.type === 'folder') {
                if (item.moduleType) return 'module-' + item.moduleType
                return 'folder'
            }
            return fileManagerService.getFileIcon(item.extension)
        },
        getItemIcon(item) {
            if (item.type === 'folder') {
                if (item.moduleIcon) return item.moduleIcon
                return fileManagerService.FILE_ICONS.folder
            }
            return fileManagerService.getFileIconSvg(item.extension)
        },
        hasThumb(item) {
            if (item.type === 'folder') return false
            const type = this.getIconType(item)
            if (type === 'image') return !!(item.thumbnailUrl || (item.publicUrl && item.publicUrl !== '#'))
            if (type === 'audio' && item.thumbnailUrl && item.meta?.has_cover) return true
            return false
        },
        getFileTypeColor(item) {
            const colors = {
                pdf: '#EF4444', document: '#3B82F6', spreadsheet: '#10B981',
                image: '#8B5CF6', archive: '#F59E0B', video: '#EC4899',
                audio: '#F97316', generic: '#6B7280',
            }
            return colors[this.getIconType(item)] || '#6B7280'
        },
        folderSizeLabel(item) {
            const files = item.fileCount || 0
            const folders = item.childrenCount || 0
            if (files && folders) return this.$t('common.fileManager.statsFilesAndFolders', { files, folders })
            if (folders) return this.$t('common.fileManager.statsFolders', { count: folders })
            return this.$t('common.fileManager.statsFiles', { count: files })
        },
        getModuleLabel(item) {
            return fileManagerService.resolveEntityLabel(item.module, item.moduleEntityId, this.$t)
        },
        navigateToEntity(item) {
            if (!item.module || !item.moduleEntityId) return
            fileManagerService.navigateEntity(item.module, item.moduleEntityId, this.$router)
        },
        onItemClick(e, item, idx) {
            e.stopPropagation()
            if (this._touchLongPressed) {
                this._touchLongPressed = false
                return
            }
            this.showSortMenu = false
            this.showMoreMenu = false

            if (this.pickerMode) {
                if (item.type === 'folder') {
                    fileManagerService.navigateToFolder(item.id)
                    this.focusedIndex = -1
                    return
                }
                if (!this.isAccepted(item)) return
                this.focusedIndex = idx
                if (this.pickerMultiple) {
                    fileManagerService.toggleSelect(item.uid)
                } else {
                    fileManagerService.selectSingle(item.uid)
                }
                this._lastClickedIndex = idx
                return
            }

            const isModifier = e.ctrlKey || e.metaKey
            const isShift = e.shiftKey
            const isVirtual = !!(item.moduleType || item.entityId)

            this.focusedIndex = idx

            if (isModifier && !isVirtual) {
                fileManagerService.toggleSelect(item.uid)
                this._lastClickedIndex = idx
            } else if (isShift && !isVirtual && this._lastClickedIndex != null && this._lastClickedIndex >= 0) {
                fileManagerService.selectRange(this._lastClickedIndex, idx, this.allItems)
            } else {
                fileManagerService.selectSingle(item.uid)
                this._lastClickedIndex = idx
                if (item.type === 'file') {
                    this.$emit('preview', item)
                }
            }
        },
        onItemDblClick(item) {
            if (item.type === 'folder') {
                fileManagerService.navigateToFolder(item.id)
                this.focusedIndex = -1
            } else if (this.getIconType(item) === 'image') {
                this.$emit('lightbox', item)
            }
        },
        onSelectionMove(folderId) {
            this.showSelectionMoveMenu = false
            const ids = [...fileManagerService.state.selectedIds]
            if (!ids.length) return
            this.$emit('moveSelected', { ids, folderId })
        },
        onItemContextMenu(event, item, idx) {
            if ('ontouchstart' in window) return
            this.focusedIndex = idx
            if (!this.isSelected(item.uid)) {
                fileManagerService.selectSingle(item.uid)
            }
            this.$emit('contextMenu', { event, item, items: this.getSelectedItems() })
        },
        onContentContextMenu(event) {
            if ('ontouchstart' in window) return
            this.$emit('contextMenu', { event, item: null, items: [] })
        },
        onContentClick(e) {
            this.showSortMenu = false
            this.showMoreMenu = false
            this.showSelectionMoveMenu = false
            if (this._lassoJustEnded) {
                this._lassoJustEnded = false
                return
            }
            if (!(e.ctrlKey || e.metaKey)) {
                fileManagerService.clearSelection()
                this.focusedIndex = -1
                this._lastClickedIndex = -1
            }
        },
        openCompactSearch() {
            this.compactSearchOpen = true
            this.$nextTick(() => this.$refs.compactSearchInput?.focus())
        },
        closeCompactSearch() {
            this.compactSearchOpen = false
            fileManagerService.search('')
        },
        onNewFolder() {
            const parentId = fileManagerService.state.currentFolderId
            if (parentId && !fileManagerService.canCreateSubfolder(parentId)) {
                this.$emit('depthLimit')
                return
            }
            const folder = fileManagerService.createFolder(this.$t('common.fileManager.newFolder'))
            this.startRename(folder.uid, folder.name)
        },
        startRename(uid, currentName) {
            this.renamingId = uid
            this.renameValue = currentName
            this.$nextTick(() => {
                const inputs = this.$refs.renameInput
                if (inputs) {
                    const input = Array.isArray(inputs) ? inputs[0] : inputs
                    if (input) {
                        input.focus()
                        input.select()
                    }
                }
            })
        },
        finishRename() {
            if (!this.renamingId) return
            const uid = this.renamingId
            const val = this.renameValue.trim()
            this.renamingId = null
            this.renameValue = ''
            if (val) {
                fileManagerService.renameItemByUid(uid, val)
            } else {
                const folder = fileManagerService.state.folders.find(f => f.uid === uid)
                if (folder && folder._pendingCreate) {
                    fileManagerService.state.folders = fileManagerService.state.folders.filter(f => f.uid !== uid)
                }
            }
            this.$nextTick(() => { if (this.$refs.contentScroll) this.$refs.contentScroll.focus() })
        },
        cancelRename() {
            const uid = this.renamingId
            this.renamingId = null
            this.renameValue = ''
            if (uid) {
                const folder = fileManagerService.state.folders.find(f => f.uid === uid)
                if (folder && folder._pendingCreate) {
                    fileManagerService.state.folders = fileManagerService.state.folders.filter(f => f.uid !== uid)
                }
            }
            this.$nextTick(() => { if (this.$refs.contentScroll) this.$refs.contentScroll.focus() })
        },
        getSelectedItems() {
            const uids = new Set(fileManagerService.state.selectedIds)
            return this.allItems.filter(i => uids.has(i.uid))
        },

        onDragStart(e, item) {
            if ('ontouchstart' in window) { e.preventDefault(); return }
            if (this._touchDragActive || this._touchDragPending) { e.preventDefault(); return }
            if (!this.isDragEnabled || item.moduleType || item.entityId) { e.preventDefault(); return }
            if (!this.isSelected(item.uid)) {
                fileManagerService.selectSingle(item.uid)
            }
            const ids = fileManagerService.state.selectedIds
            e.dataTransfer.setData('application/fm-items', JSON.stringify(ids))
            e.dataTransfer.effectAllowed = 'move'

            const ghost = this._createDragGhost(item, ids.length)
            ghost.style.position = 'fixed'
            ghost.style.zIndex = '200010'
            ghost.style.left = (e.clientX - 90) + 'px'
            ghost.style.top = (e.clientY - 22) + 'px'
            document.body.appendChild(ghost)
            this._desktopDragGhost = ghost

            const empty = document.createElement('div')
            empty.style.cssText = 'width:1px;height:1px;position:fixed;top:-9999px;left:-9999px;opacity:0.01'
            document.body.appendChild(empty)
            e.dataTransfer.setDragImage(empty, 0, 0)
            setTimeout(() => empty.remove(), 0)

            this._onDesktopDragMove = (ev) => {
                if (this._desktopDragGhost) {
                    this._desktopDragGhost.style.left = (ev.clientX - 90) + 'px'
                    this._desktopDragGhost.style.top = (ev.clientY - 22) + 'px'
                }
            }
            document.addEventListener('drag', this._onDesktopDragMove)

            if (!('ontouchstart' in window)) this._startDragAutoScroll()
        },
        onItemDragOver(e, item) {
            if (item.type !== 'folder' || item.moduleType || item.entityId) return
            const hasFmItems = e.dataTransfer.types.includes('application/fm-items')
            const hasFmFolder = e.dataTransfer.types.includes('fm-folder-id')
            if (!hasFmItems && !hasFmFolder) return
            if (hasFmItems) {
                const ids = fileManagerService.state.selectedIds
                const blocked = ids.some(uid => {
                    const f = fileManagerService.state.folders.find(fl => fl.uid === uid)
                    return f && (fileManagerService.isDescendantOf(item.id, f.id) || f.id === item.id)
                })
                if (blocked) return
            }
            if (hasFmFolder) {
                const dragId = fileManagerService.state._draggingSidebarFolderId
                if (dragId === item.id) return
                if (dragId && fileManagerService.isDescendantOf(item.id, dragId)) return
            }
            e.dataTransfer.dropEffect = 'move'
            this.dropTargetId = item.id
        },
        onItemDragLeave(item) {
            if (this.dropTargetId === item.id) {
                this.dropTargetId = null
            }
        },
        onContentDragOver(e) {
            if (!e.dataTransfer.types.includes('fm-folder-id')) return
            const dragId = fileManagerService.state._draggingSidebarFolderId
            const targetId = fileManagerService.state.currentFolderId
            if (targetId) {
                const folder = fileManagerService.state.folders.find(f => f.id === targetId)
                if (!folder || folder.moduleType || folder.entityId) return
            }
            if (dragId) {
                const dragged = fileManagerService.state.folders.find(f => f.id === dragId)
                if (dragged && (dragged.parentId || null) === (targetId || null)) return
            }
            e.dataTransfer.dropEffect = 'move'
            this.contentDropHighlight = true
        },
        onContentDragLeave(e) {
            const scrollEl = this.$refs.contentScroll
            if (scrollEl && !scrollEl.contains(e.relatedTarget)) {
                this.contentDropHighlight = false
            }
        },
        onContentDrop(e) {
            this.contentDropHighlight = false
            const folderUid = e.dataTransfer.getData('fm-folder-id')
            if (!folderUid) return
            const draggedFolder = fileManagerService.state.folders.find(f => f.uid === folderUid)
            if (!draggedFolder) return
            const targetId = fileManagerService.state.currentFolderId
            if (draggedFolder.id === targetId) return
            if ((draggedFolder.parentId || null) === (targetId || null)) return
            if (targetId) {
                const target = fileManagerService.state.folders.find(f => f.id === targetId)
                if (!target || target.moduleType || target.entityId) return
                if (fileManagerService.isDescendantOf(targetId, draggedFolder.id)) return
                const subtreeDepth = this._getSubtreeDepth(draggedFolder.id)
                const targetDepth = this._getFolderDepth(targetId)
                if (targetDepth + 1 + subtreeDepth >= fileManagerService.MAX_FOLDER_DEPTH) {
                    this.$emit('depthLimit')
                    return
                }
            }
            fileManagerService.moveFolderToParent(draggedFolder.id, targetId)
        },
        _getFolderDepth(folderId) {
            let depth = 0
            let id = folderId
            while (id) {
                const f = fileManagerService.state.folders.find(fl => fl.id === id)
                if (!f || !f.parentId) break
                depth++
                id = f.parentId
            }
            return depth
        },
        _getSubtreeDepth(folderId) {
            const children = fileManagerService.state.folders.filter(f => f.parentId === folderId)
            if (!children.length) return 0
            let max = 0
            for (const child of children) max = Math.max(max, 1 + this._getSubtreeDepth(child.id))
            return max
        },
        onItemDrop(e, item) {
            this._stopDragAutoScroll()
            this.dropTargetId = null
            if (item.type !== 'folder' || item.moduleType || item.entityId) return

            const folderUid = e.dataTransfer.getData('fm-folder-id')
            if (folderUid) {
                const draggedFolder = fileManagerService.state.folders.find(f => f.uid === folderUid)
                if (!draggedFolder || draggedFolder.id === item.id) return
                if (fileManagerService.isDescendantOf(item.id, draggedFolder.id)) return
                const subtreeDepth = this._getSubtreeDepth(draggedFolder.id)
                const targetDepth = this._getFolderDepth(item.id)
                if (targetDepth + 1 + subtreeDepth >= fileManagerService.MAX_FOLDER_DEPTH) {
                    this.$emit('depthLimit')
                    return
                }
                fileManagerService.moveFolderToParent(draggedFolder.id, item.id)
                return
            }

            const raw = e.dataTransfer.getData('application/fm-items')
            if (!raw) return
            try {
                const uids = JSON.parse(raw)
                if (uids.includes(item.uid)) return
                const hasFolders = uids.some(uid => {
                    const f = fileManagerService.state.folders.find(fl => fl.uid === uid)
                    return !!f
                })
                if (hasFolders) {
                    const blocked = uids.some(uid => {
                        const f = fileManagerService.state.folders.find(fl => fl.uid === uid)
                        return f && (fileManagerService.isDescendantOf(item.id, f.id) || f.id === item.id)
                    })
                    if (blocked) return
                    if (!fileManagerService.canCreateSubfolder(item.id)) {
                        this.$emit('depthLimit')
                        return
                    }
                }
                fileManagerService.moveItems(uids, item.id)
            } catch { /* empty */ }
        },

        onKeyDown(e) {
            if (this.renamingId) return

            if (e.altKey && e.key === 'ArrowLeft') {
                e.preventDefault()
                fileManagerService.goBack()
                return
            }
            if (e.altKey && e.key === 'ArrowRight') {
                e.preventDefault()
                fileManagerService.goForward()
                return
            }

            const len = this.allItems.length
            if (!len) return

            const scrollToFocused = (idx) => {
                this.$nextTick(() => {
                    const scroll = this.$refs.contentScroll
                    if (!scroll) return
                    const items = scroll.querySelectorAll('.fm-grid-item, .fm-list-item')
                    const el = items[idx]
                    if (el) el.scrollIntoView({ block: 'nearest' })
                })
            }

            const step = (key) => {
                if (key === 'ArrowLeft' || key === 'ArrowRight') return 1
                if (fileManagerService.state.viewMode !== 'grid') return 1
                const grid = this.$el.querySelector('.fm-grid')
                if (!grid || !grid.children.length) return 1
                const firstTop = grid.children[0].getBoundingClientRect().top
                let cols = 1
                for (let i = 1; i < grid.children.length; i++) {
                    if (grid.children[i].getBoundingClientRect().top !== firstTop) break
                    cols++
                }
                return cols
            }

            switch (e.key) {
                case 'ArrowDown':
                case 'ArrowRight': {
                    e.preventDefault()
                    const s = step(e.key)
                    const newIdx = Math.min(this.focusedIndex + s, len - 1)
                    if (newIdx === this.focusedIndex) break
                    this.focusedIndex = newIdx
                    const item = this.allItems[newIdx]
                    if (!item) break
                    if (e.shiftKey && !item.moduleType && !item.entityId) {
                        if (this._lastClickedIndex == null || this._lastClickedIndex < 0) this._lastClickedIndex = 0
                        fileManagerService.selectRange(this._lastClickedIndex, newIdx, this.allItems)
                    } else if (!e.ctrlKey && !e.metaKey) {
                        fileManagerService.selectSingle(item.uid)
                        this._lastClickedIndex = newIdx
                    }
                    scrollToFocused(newIdx)
                    break
                }
                case 'ArrowUp':
                case 'ArrowLeft': {
                    e.preventDefault()
                    const s = step(e.key)
                    const newIdx = Math.max(this.focusedIndex - s, 0)
                    if (newIdx === this.focusedIndex) break
                    this.focusedIndex = newIdx
                    const item = this.allItems[newIdx]
                    if (!item) break
                    if (e.shiftKey && !item.moduleType && !item.entityId) {
                        if (this._lastClickedIndex == null || this._lastClickedIndex < 0) this._lastClickedIndex = 0
                        fileManagerService.selectRange(this._lastClickedIndex, newIdx, this.allItems)
                    } else if (!e.ctrlKey && !e.metaKey) {
                        fileManagerService.selectSingle(item.uid)
                        this._lastClickedIndex = newIdx
                    }
                    scrollToFocused(newIdx)
                    break
                }
                case 'Enter': {
                    e.preventDefault()
                    const item = this.allItems[this.focusedIndex]
                    if (!item) break
                    if (item.type === 'folder') {
                        fileManagerService.navigateToFolder(item.id)
                        this.focusedIndex = -1
                    } else {
                        this.$emit('preview', item)
                    }
                    break
                }
                case 'Backspace': {
                    e.preventDefault()
                    if (fileManagerService.isTrashMode.value) {
                        if (fileManagerService.state.trashFolderId) {
                            fileManagerService.navigateToFolder(null)
                            this.focusedIndex = -1
                        }
                    } else if (fileManagerService.state.currentFolderId) {
                        const current = fileManagerService.state.folders.find(
                            f => f.id === fileManagerService.state.currentFolderId
                        )
                        fileManagerService.navigateToFolder(current?.parentId || null)
                        this.focusedIndex = -1
                    }
                    break
                }
                case 'Escape': {
                    e.preventDefault()
                    fileManagerService.clearSelection()
                    this.focusedIndex = -1
                    this._lastClickedIndex = -1
                    break
                }
                case 'F2': {
                    e.preventDefault()
                    if (fileManagerService.isTrashMode.value) break
                    const sel = fileManagerService.state.selectedIds
                    if (sel.length !== 1) break
                    const target = this.allItems.find(i => i.uid === sel[0])
                    if (!target) break
                    if (this.pickerMode) {
                        if (!this.pickerEntityFolder || target.type !== 'file') break
                    } else {
                        if (target.type === 'folder' && (target.moduleType || target.entityId)) break
                    }
                    this.startRename(target.uid, target.name)
                    break
                }
                case 'Delete': {
                    e.preventDefault()
                    if (this.pickerMode) break
                    const ids = [...fileManagerService.state.selectedIds]
                    if (!ids.length) break
                    if (fileManagerService.isTrashMode.value) {
                        this.$emit('permanentDelete', ids)
                    } else {
                        this.$emit('deleteItems', ids)
                    }
                    break
                }
                case 'a': {
                    if (e.metaKey || e.ctrlKey) {
                        e.preventDefault()
                        if (this.pickerMode && this.pickerAccept) {
                            const accepted = this.allItems.filter(i => this.isAccepted(i) && !i.moduleType && !i.entityId && i.type === 'file')
                            fileManagerService.state.selectedIds = accepted.map(i => i.uid)
                        } else {
                            fileManagerService.selectAll()
                        }
                    }
                    break
                }
            }
        },

        onContentMouseDown(e) {
            if ('ontouchstart' in window) return
            if (e.button !== 0) return
            const target = e.target
            if (target.closest('.fm-grid-item, .fm-list-row, .fm-toolbar, .fm-list-header, input')) return

            const scrollEl = this.$refs.contentScroll
            if (!scrollEl) return

            const rect = scrollEl.getBoundingClientRect()
            const startX = e.clientX - rect.left + scrollEl.scrollLeft
            const startY = e.clientY - rect.top + scrollEl.scrollTop
            const additive = e.ctrlKey || e.metaKey
            const prevSelection = additive ? [...fileManagerService.state.selectedIds] : []

            this._lassoStartX = startX
            this._lassoStartY = startY
            this._lassoScrollEl = scrollEl
            this._lassoScrollRect = rect
            this._lassoAdditive = additive
            this._lassoPrevSelection = prevSelection
            this._lassoMoved = false

            const onMouseMove = (ev) => {
                const cx = ev.clientX - rect.left + scrollEl.scrollLeft
                const cy = ev.clientY - rect.top + scrollEl.scrollTop
                const dx = Math.abs(cx - startX)
                const dy = Math.abs(cy - startY)
                if (!this._lassoMoved && dx < 5 && dy < 5) return
                this._lassoMoved = true
                this.lassoActive = true

                const left = Math.min(startX, cx)
                const top = Math.min(startY, cy)
                const width = Math.abs(cx - startX)
                const height = Math.abs(cy - startY)
                this.lassoRect = {left, top, width, height}

                this._updateLassoSelection(left, top, width, height, scrollEl, prevSelection)
            }

            const onMouseUp = () => {
                document.removeEventListener('mousemove', onMouseMove)
                document.removeEventListener('mouseup', onMouseUp)
                if (this._lassoMoved) {
                    this._lassoJustEnded = true
                }
                this.lassoActive = false
                this.lassoRect = null
            }

            document.addEventListener('mousemove', onMouseMove)
            document.addEventListener('mouseup', onMouseUp)
        },
        _updateLassoSelection(left, top, width, height, scrollEl, prevSelection) {
            const lassoRight = left + width
            const lassoBottom = top + height
            const items = scrollEl.querySelectorAll('[data-uid]')
            const selected = new Set(prevSelection)

            items.forEach(el => {
                const uid = el.dataset.uid
                if (!uid) return
                const item = this.allItems.find(i => i.uid === uid)
                if (!item || item.moduleType || item.entityId) return
                if (this.pickerMode && !this.isAccepted(item)) return

                const elRect = el.getBoundingClientRect()
                const scrollRect = scrollEl.getBoundingClientRect()
                const elLeft = elRect.left - scrollRect.left + scrollEl.scrollLeft
                const elTop = elRect.top - scrollRect.top + scrollEl.scrollTop
                const elRight = elLeft + elRect.width
                const elBottom = elTop + elRect.height

                const intersects = elLeft < lassoRight && elRight > left && elTop < lassoBottom && elBottom > top
                if (intersects) {
                    selected.add(uid)
                } else if (!prevSelection.includes(uid)) {
                    selected.delete(uid)
                }
            })

            fileManagerService.state.selectedIds = [...selected]
        },

        _calcPageLimit() {
            const el = this.$refs.contentScroll
            if (!el) return
            fileManagerService.updatePageLimit(el.clientHeight, el.clientWidth, fileManagerService.state.viewMode)
        },
        _reobserveSentinel() {
            const obs = this.$options._infiniteObs
            const sentinel = this.$refs.infiniteSentinel
            if (obs && sentinel) {
                obs.unobserve(sentinel)
                obs.observe(sentinel)
            }
        },

        _startDragAutoScroll() {
            this._dragMouseY = 0
            const onDrag = (e) => { this._dragMouseY = e.clientY }
            const onEnd = () => {
                document.removeEventListener('dragover', onDrag, true)
                document.removeEventListener('dragend', onEnd)
                document.removeEventListener('drop', onEnd)
                cancelAnimationFrame(this._dragScrollRAF)
                this._dragScrollRAF = null
                if (this._desktopDragGhost) {
                    this._desktopDragGhost.remove()
                    this._desktopDragGhost = null
                }
                if (this._onDesktopDragMove) {
                    document.removeEventListener('drag', this._onDesktopDragMove)
                    this._onDesktopDragMove = null
                }
            }
            document.addEventListener('dragover', onDrag, true)
            document.addEventListener('dragend', onEnd)
            document.addEventListener('drop', onEnd)
            this._dragScrollCleanup = onEnd

            const tick = () => {
                const scrollEl = this.$refs.contentScroll
                if (scrollEl && this._dragMouseY) {
                    const rect = scrollEl.getBoundingClientRect()
                    const y = this._dragMouseY
                    const edge = 40
                    if (y < rect.top + edge && scrollEl.scrollTop > 0) {
                        scrollEl.scrollTop -= 6
                    } else if (y > rect.bottom - edge && scrollEl.scrollTop < scrollEl.scrollHeight - scrollEl.clientHeight) {
                        scrollEl.scrollTop += 6
                    }
                }
                this._dragScrollRAF = requestAnimationFrame(tick)
            }
            this._dragScrollRAF = requestAnimationFrame(tick)
        },
        _stopDragAutoScroll() {
            if (this._dragScrollCleanup) {
                this._dragScrollCleanup()
                this._dragScrollCleanup = null
            }
        },

        _initTouchLongPress() {
            if (!('ontouchstart' in window)) return
            const rootEl = this.$el
            if (!rootEl) return
            const scrollEl = this.$refs.contentScroll

            this._touchHandler = (e) => {
                const itemEl = e.target.closest('.fm-grid-item, .fm-list-row')
                const uid = itemEl ? itemEl.dataset.uid : null
                const isInContent = !itemEl && scrollEl && scrollEl.contains(e.target) && !e.target.closest('.fm-toolbar, .fm-list-header, .fm-picker-bar, input')
                if (!itemEl && !isInContent) return

                const touch = e.touches[0]
                this._lpStartX = touch.clientX
                this._lpStartY = touch.clientY
                this._lpReady = false
                this._lpUid = uid
                this._lpItemEl = itemEl
                this._lpIsEmpty = !itemEl
                const startScroll = scrollEl ? scrollEl.scrollTop : 0

                clearTimeout(this._lpTimer)
                this._lpTimer = setTimeout(() => {
                    this._lpTimer = null
                    if (scrollEl && Math.abs(scrollEl.scrollTop - startScroll) > 5) return
                    this._lpReady = true
                    if (navigator.vibrate) navigator.vibrate(15)
                }, 500)
            }

            this._touchMoveHandler = (e) => {
                if (!this._lpTimer && !this._lpReady) return
                const touch = e.touches[0]
                const dx = Math.abs(touch.clientX - this._lpStartX)
                const dy = Math.abs(touch.clientY - this._lpStartY)
                if (this._lpTimer && (dx > 10 || dy > 10)) {
                    clearTimeout(this._lpTimer)
                    this._lpTimer = null
                    return
                }
                if (this._lpReady && (dx > 15 || dy > 15)) {
                    this._lpReady = false
                    if (this._lpIsEmpty) return
                    const item = this.allItems.find(i => i.uid === this._lpUid)
                    if (item && this.isDragEnabled && !item.moduleType && !item.entityId) {
                        this._touchLongPressed = true
                        const idx = this.allItems.indexOf(item)
                        this.focusedIndex = idx
                        if (!this.isSelected(item.uid)) {
                            fileManagerService.selectSingle(item.uid)
                        }
                        this._touchDragPending = true
                        this._touchItem = item
                        this._touchCardEl = this._lpItemEl
                        this._touchDragStartX = this._lpStartX
                        this._touchDragStartY = this._lpStartY
                        this.$emit('closeContextMenu')
                        this._startTouchDrag(item, touch.clientX, touch.clientY)
                        document.addEventListener('touchmove', this._onDocTouchDragMove, { passive: false })
                        document.addEventListener('touchend', this._onDocTouchDragEnd)
                        document.addEventListener('touchcancel', this._onDocTouchDragEnd)
                    }
                }
            }

            this._touchEndHandler = (e) => {
                clearTimeout(this._lpTimer)
                this._lpTimer = null
                if (this._lpReady) {
                    this._lpReady = false
                    const touch = e.changedTouches[0]
                    const dx = Math.abs(touch.clientX - this._lpStartX)
                    const dy = Math.abs(touch.clientY - this._lpStartY)
                    if (dx < 40 && dy < 40) {
                        e.preventDefault()
                        this._touchLongPressed = true
                        if (this._lpIsEmpty) {
                            this.$emit('contextMenu', {
                                event: { clientX: this._lpStartX, clientY: this._lpStartY, preventDefault() {} },
                                item: null,
                                items: [],
                            })
                            return
                        }
                        const item = this.allItems.find(i => i.uid === this._lpUid)
                        if (!item) return
                        const idx = this.allItems.indexOf(item)
                        this.focusedIndex = idx
                        if (!this.isSelected(item.uid)) {
                            fileManagerService.selectSingle(item.uid)
                        }
                        this.$emit('contextMenu', {
                            event: { clientX: this._lpStartX, clientY: this._lpStartY, preventDefault() {} },
                            item,
                            items: this.getSelectedItems(),
                        })
                    }
                }
            }

            rootEl.addEventListener('touchstart', this._touchHandler, { passive: true })
            rootEl.addEventListener('touchmove', this._touchMoveHandler, { passive: true })
            rootEl.addEventListener('touchend', this._touchEndHandler, { passive: false })
            this._touchRootEl = rootEl
        },
        _destroyTouchLongPress() {
            clearTimeout(this._lpTimer)
            if (this._touchRootEl) {
                this._touchRootEl.removeEventListener('touchstart', this._touchHandler)
                this._touchRootEl.removeEventListener('touchmove', this._touchMoveHandler)
                this._touchRootEl.removeEventListener('touchend', this._touchEndHandler)
            }
            this._cleanupTouchDrag()
        },

        _onDocTouchDragMove(e) {
            const touch = e.touches[0]
            if (this._touchDragPending && !this._touchDragActive) {
                e.preventDefault()
                const dx = Math.abs(touch.clientX - this._touchDragStartX)
                const dy = Math.abs(touch.clientY - this._touchDragStartY)
                if (dx < 15 && dy < 15) return
                this.$emit('closeContextMenu')
                this._startTouchDrag(this._touchItem, touch.clientX, touch.clientY)
                return
            }
            if (this._touchDragActive) {
                e.preventDefault()
                this._updateTouchDragPosition(touch.clientX, touch.clientY)
            }
        },
        _onDocTouchDragEnd(e) {
            if (this._touchDragActive) {
                const touch = e.changedTouches[0]
                this._finishTouchDrag(touch.clientX, touch.clientY)
            } else {
                this._cleanupTouchDrag()
            }
        },

        _createDragGhost(item, count) {
            const panelEl = this.$el.closest('.panel-app') || this.$el
            const cs = getComputedStyle(panelEl)
            const bg = cs.getPropertyValue('--panel-bg').trim() || '#ffffff'
            const textColor = cs.getPropertyValue('--panel-text-primary').trim() || '#333'

            const isTouch = 'ontouchstart' in window
            const ghost = document.createElement('div')
            ghost.style.cssText = 'position:fixed;top:-9999px;left:-9999px;pointer-events:none;' +
                'width:180px;height:44px;display:flex;align-items:center;gap:8px;padding:0 12px;box-sizing:border-box;' +
                'background:' + bg + ';border-radius:' + (isTouch ? '0' : '10px') + ';box-shadow:0 4px 16px rgba(0,0,0,0.18);opacity:0.9;' +
                'font-family:-apple-system,BlinkMacSystemFont,sans-serif;'

            const iconEl = document.createElement('span')
            iconEl.style.cssText = 'width:24px;height:24px;flex-shrink:0;display:flex;align-items:center;justify-content:center'
            if (item.type === 'folder') {
                iconEl.style.color = item.color || '#3B82F6'
                iconEl.innerHTML = '<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" stroke="none"><path d="M2 6a2 2 0 0 1 2-2h4.59a1 1 0 0 1 .7.29L11 6H20a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6z"/></svg>'
            } else {
                iconEl.style.color = this.getFileTypeColor(item)
                iconEl.innerHTML = this.getItemIcon(item)
                const svg = iconEl.querySelector('svg')
                if (svg) { svg.setAttribute('width', '20'); svg.setAttribute('height', '20') }
            }
            ghost.appendChild(iconEl)

            const nameEl = document.createElement('span')
            nameEl.textContent = item.name
            nameEl.style.cssText = 'overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size:13px;font-weight:500;color:' + textColor + ';flex:1;min-width:0'
            ghost.appendChild(nameEl)

            if (count > 1) {
                const badge = document.createElement('span')
                badge.textContent = count
                badge.style.cssText = 'position:absolute;top:-6px;right:-6px;display:inline-flex;align-items:center;justify-content:center;min-width:20px;height:20px;padding:0 5px;background:#007AFF;color:#fff;border-radius:10px;font-size:11px;font-weight:600;box-shadow:0 2px 6px rgba(0,122,255,0.4);z-index:1'
                ghost.appendChild(badge)
            }

            return ghost
        },
        _startTouchDrag(item, x, y) {
            this._touchDragActive = true
            this._touchDragPending = false
            this.touchDragReadyId = null

            const scrollEl = this.$refs.contentScroll
            if (scrollEl) {
                this._prevTouchAction = scrollEl.style.touchAction
                this._prevOverflow = scrollEl.style.overflowY
                scrollEl.style.touchAction = 'none'
                scrollEl.style.overflowY = 'hidden'
            }

            this._touchLastX = x
            this._touchLastY = y

            const count = fileManagerService.state.selectedIds.length
            const ghost = this._createDragGhost(item, count)
            const w = 180
            const h = 44
            ghost.style.position = 'fixed'
            ghost.style.left = (x - w / 2) + 'px'
            ghost.style.top = (y - h / 2) + 'px'
            ghost.style.zIndex = '200010'
            document.body.appendChild(ghost)
            this._dragGhost = ghost
            this._dragGhostW = w
            this._dragGhostH = h
            document.body.style.userSelect = 'none'
            document.body.style.webkitUserSelect = 'none'
            this._dragHighlightEl = null

            this._startTouchAutoScroll()
        },
        _startTouchAutoScroll() {
            const tick = () => {
                const scrollEl = this.$refs.contentScroll
                if (scrollEl && this._touchLastY) {
                    const rect = scrollEl.getBoundingClientRect()
                    const y = this._touchLastY
                    if (y < rect.top + 40 && scrollEl.scrollTop > 0) scrollEl.scrollTop -= 8
                    else if (y > rect.bottom - 40 && scrollEl.scrollTop < scrollEl.scrollHeight - scrollEl.clientHeight) scrollEl.scrollTop += 8
                }
                this._touchScrollRAF = requestAnimationFrame(tick)
            }
            this._touchScrollRAF = requestAnimationFrame(tick)
        },
        _stopTouchAutoScroll() {
            if (this._touchScrollRAF) {
                cancelAnimationFrame(this._touchScrollRAF)
                this._touchScrollRAF = null
            }
        },
        _updateTouchDragPosition(x, y) {
            this._touchLastX = x
            this._touchLastY = y

            if (this._dragGhost) {
                this._dragGhost.style.left = (x - this._dragGhostW / 2) + 'px'
                this._dragGhost.style.top = (y - this._dragGhostH / 2) + 'px'
            }

            const el = document.elementFromPoint(x, y)
            if (!el) return

            const sidebarFolder = el.closest('[data-sidebar-folder-id]')
            const trashEl = el.closest('.fm-sidebar-trash')
            const contentFolder = !sidebarFolder ? el.closest('[data-folder-id]') : null
            const target = sidebarFolder || contentFolder || trashEl

            if (target !== this._dragHighlightEl) {
                if (this._dragHighlightEl) this._dragHighlightEl.classList.remove('fm-touch-drop-target')
                if (target) {
                    const selectedIds = fileManagerService.state.selectedIds
                    let canDrop = false
                    if (contentFolder) {
                        const fid = contentFolder.dataset.folderId
                        const found = this.allItems.find(i => i.type === 'folder' && String(i.id) === fid)
                        if (found && !found.moduleType && !found.entityId) {
                            const blocked = selectedIds.some(uid => {
                                const f = fileManagerService.state.folders.find(fl => fl.uid === uid)
                                return f && (fileManagerService.isDescendantOf(found.id, f.id) || f.id === found.id)
                            })
                            canDrop = !blocked
                        }
                    } else if (sidebarFolder) {
                        const fid = parseInt(sidebarFolder.dataset.sidebarFolderId, 10)
                        const blocked = selectedIds.some(uid => {
                            const f = fileManagerService.state.folders.find(fl => fl.uid === uid)
                            return f && (fileManagerService.isDescendantOf(fid, f.id) || f.id === fid)
                        })
                        canDrop = !blocked
                    } else if (trashEl) {
                        canDrop = true
                    }
                    if (canDrop) target.classList.add('fm-touch-drop-target')
                }
                this._dragHighlightEl = target || null
            }
        },
        _finishTouchDrag(x, y) {
            const el = document.elementFromPoint(x, y)
            const selectedIds = [...fileManagerService.state.selectedIds]

            if (el) {
                const sidebarFolder = el.closest('[data-sidebar-folder-id]')
                const trashEl = el.closest('.fm-sidebar-trash')
                const contentFolder = !sidebarFolder ? el.closest('[data-folder-id]') : null

                if (trashEl && selectedIds.length) {
                    this.$emit('deleteItems', selectedIds)
                } else if (contentFolder) {
                    const folderId = parseInt(contentFolder.dataset.folderId, 10)
                    const folder = this.allItems.find(i => i.type === 'folder' && i.id === folderId)
                    if (folder && !folder.moduleType && !folder.entityId && !selectedIds.includes(folder.uid)) {
                        const hasFolders = selectedIds.some(uid => fileManagerService.state.folders.some(fl => fl.uid === uid))
                        if (hasFolders) {
                            const blocked = selectedIds.some(uid => {
                                const f = fileManagerService.state.folders.find(fl => fl.uid === uid)
                                return f && (fileManagerService.isDescendantOf(folderId, f.id) || f.id === folderId)
                            })
                            if (blocked) { /* empty */ }
                            else if (!fileManagerService.canCreateSubfolder(folderId)) this.$emit('depthLimit')
                            else fileManagerService.moveItems(selectedIds, folderId)
                        } else {
                            fileManagerService.moveItems(selectedIds, folderId)
                        }
                    }
                } else if (sidebarFolder) {
                    const folderId = parseInt(sidebarFolder.dataset.sidebarFolderId, 10)
                    if (!isNaN(folderId)) {
                        const hasFolders = selectedIds.some(uid => fileManagerService.state.folders.some(fl => fl.uid === uid))
                        if (hasFolders) {
                            const blocked = selectedIds.some(uid => {
                                const f = fileManagerService.state.folders.find(fl => fl.uid === uid)
                                return f && (fileManagerService.isDescendantOf(folderId, f.id) || f.id === folderId)
                            })
                            if (blocked) { /* empty */ }
                            else if (!fileManagerService.canCreateSubfolder(folderId)) this.$emit('depthLimit')
                            else fileManagerService.moveItems(selectedIds, folderId)
                        } else {
                            fileManagerService.moveItems(selectedIds, folderId)
                        }
                    }
                }
            }
            this._cleanupTouchDrag()
        },
        _cleanupTouchDrag() {
            this._stopTouchAutoScroll()
            this._touchDragActive = false
            this._touchDragPending = false
            this.touchDragReadyId = null
            if (this._dragHighlightEl) {
                this._dragHighlightEl.classList.remove('fm-touch-drop-target')
                this._dragHighlightEl = null
            }
            if (this._dragGhost) {
                this._dragGhost.remove()
                this._dragGhost = null
            }
            this._touchItem = null
            const scrollEl = this.$refs.contentScroll
            if (scrollEl) {
                scrollEl.style.touchAction = this._prevTouchAction || ''
                scrollEl.style.overflowY = this._prevOverflow || ''
            }
            document.body.style.userSelect = ''
            document.body.style.webkitUserSelect = ''
            document.removeEventListener('touchmove', this._onDocTouchDragMove)
            document.removeEventListener('touchend', this._onDocTouchDragEnd)
            document.removeEventListener('touchcancel', this._onDocTouchDragEnd)
        },
    },
}
</script>
